import { Button, Center, Loader, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconLock, IconShieldCheck } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router';
import { ApiError, api } from './lib/api';
import { auth, login, logout, resetSilentLogin, silentLoginTried, trySilentLogin } from './lib/auth';
import { MeContext } from './lib/me';
import type { AdminRole, Me } from './lib/types';
import { ApprovalReviewPage, ApprovalsPage } from './pages/admin/Approvals';
import { AssignmentPage, AssignmentsPage } from './pages/admin/Assignments';
import { AuditPage } from './pages/admin/Audit';
import { ContentCoursesPage } from './pages/admin/ContentCourses';
import { VersionEditorPage } from './pages/admin/VersionEditor';
import { DashboardPage } from './pages/admin/Dashboard';
import { ResultDetailPage, ResultsPage } from './pages/admin/Results';
import { UsersPage } from './pages/admin/Users';
import { CourseViewPage } from './pages/employee/CourseView';
import { HistoryPage } from './pages/employee/History';
import { MyCoursesPage } from './pages/employee/MyCourses';
import { ProfilePage } from './pages/employee/Profile';
import { QuizPage } from './pages/employee/Quiz';
import { ResultPage } from './pages/employee/Result';
import { AdminGate } from './ui/AdminGate';
import { IncidentButton } from './ui/Incident';
import { ThemeToggle } from './ui/ThemeToggle';
import { HelpPage } from './pages/Help';
import { IncidentsPage } from './pages/admin/Incidents';
import { Layout } from './ui/Layout';

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <Center mih="100vh" p="md" pos="relative">
      <div style={{ position: 'absolute', top: 16, right: 16 }}><ThemeToggle /></div>
      <Paper withBorder shadow="sm" radius="lg" p="xl" maw={440} w="100%">{children}</Paper>
    </Center>
  );
}

function LoginScreen() {
  const returnTo = location.pathname + location.search;
  return (
    <Splash>
      <Stack align="center" gap="md" ta="center">
        <ThemeIcon size={64} radius="xl" variant="light"><IconShieldCheck size={36} /></ThemeIcon>
        <Title order={2}>Chiron</Title>
        <Text c="dimmed">Корпоративное обучение кибербезопасности. Вход только через корпоративный SSO.</Text>
        <Button size="md" fullWidth onClick={() => login(returnTo)}>Войти через корпоративный SSO</Button>
        <IncidentButton authed={false} />
      </Stack>
    </Splash>
  );
}

function NoAccess({ message }: { message: string }) {
  return (
    <Splash>
      <Stack align="center" gap="md" ta="center">
        <ThemeIcon size={64} radius="xl" color="red" variant="light"><IconLock size={34} /></ThemeIcon>
        <Title order={3}>Нет доступа к обучению</Title>
        <Text c="dimmed">{message}</Text>
        <Button variant="default" onClick={() => logout()}>Выйти</Button>
        <IncidentButton authed={false} />
      </Stack>
    </Splash>
  );
}

// Код авторизации одноразовый: StrictMode вызывает эффект дважды, обмен должен пройти один раз
let callbackOnce: ReturnType<ReturnType<typeof auth>['signinRedirectCallback']> | null = null;

function Callback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    callbackOnce ??= auth().signinRedirectCallback();
    callbackOnce
      .then((u) => {
        resetSilentLogin();
        navigate(typeof u.state === 'string' ? u.state : '/', { replace: true });
      })
      .catch((e: Error & { error?: string; state?: unknown }) => {
        // тихий вход не удался (сессии SSO нет) - показать обычный экран входа, ссылку сохранить
        if (['login_required', 'interaction_required', 'consent_required'].includes(e.error ?? '')) {
          callbackOnce = null;
          navigate(typeof e.state === 'string' ? e.state : '/', { replace: true });
        } else setError(e.message);
      });
  }, [navigate]);
  if (error) return <Splash><Stack><Text c="red">Ошибка входа: {error}</Text><Button onClick={() => login('/')}>Попробовать снова</Button></Stack></Splash>;
  return <Center mih="100vh"><Loader /></Center>;
}

function RoleRoute({ roles, me, children }: { roles: AdminRole[]; me: Me; children: React.ReactElement }) {
  return roles.some((r) => me.roles.includes(r)) ? children : <Navigate to="/" replace />;
}

function Authed() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/me'), retry: false });
  if (me.isLoading) return <Center mih="100vh"><Loader /></Center>;
  if (me.error) {
    const e = me.error as ApiError;
    if (e.status === 403) return <NoAccess message={e.message} />;
    return <Splash><Text c="red">{e.message}</Text></Splash>;
  }
  const m = me.data!;
  const R = (roles: AdminRole[], el: React.ReactElement) => <RoleRoute roles={roles} me={m}>{el}</RoleRoute>;
  return (
    <MeContext.Provider value={m}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<MyCoursesPage />} />
          <Route path="history" element={<HistoryPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="learn/:assignmentId" element={<CourseViewPage />} />
          <Route path="learn/:assignmentId/lesson/:lessonId" element={<CourseViewPage />} />
          <Route path="learn/:assignmentId/quiz" element={<QuizPage />} />
          <Route path="results/:attemptId" element={<ResultPage />} />
          {m.roles.length > 0 && (
            <Route path="admin" element={<AdminGate />}>
              <Route index element={<DashboardPage />} />
              <Route path="content" element={R(['CONTENT'], <ContentCoursesPage />)} />
              <Route path="content/versions/:versionId" element={R(['CONTENT'], <VersionEditorPage />)} />
              <Route path="approvals" element={R(['CONTENT'], <ApprovalsPage />)} />
              <Route path="approvals/:versionId" element={R(['CONTENT'], <ApprovalReviewPage />)} />
              <Route path="assignments" element={R(['ASSIGNMENTS'], <AssignmentsPage />)} />
              <Route path="assignments/:id" element={R(['ASSIGNMENTS'], <AssignmentPage />)} />
              <Route path="results" element={R(['RESULTS'], <ResultsPage />)} />
              <Route path="results/:id" element={R(['RESULTS'], <ResultDetailPage />)} />
              <Route path="incidents" element={R(['RESULTS'], <IncidentsPage />)} />
              <Route path="users" element={R(['ASSIGNMENTS', 'RESULTS'], <UsersPage />)} />
              <Route path="audit" element={R(['RESULTS'], <AuditPage />)} />
            </Route>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </MeContext.Provider>
  );
}

function Gate() {
  const [state, setState] = useState<'loading' | 'in' | 'out'>('loading');
  useEffect(() => {
    auth().getUser().then((u) => {
      if (u) setState('in');
      else if (!silentLoginTried()) void trySilentLogin();
      else setState('out');
    });
  }, []);
  if (state === 'loading') return <Center mih="100vh"><Loader /></Center>;
  return state === 'in' ? <Authed /> : <LoginScreen />;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth/callback" element={<Callback />} />
        <Route path="*" element={<Gate />} />
      </Routes>
    </BrowserRouter>
  );
}
